import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { VerticalCard } from '../../partials/cards/verticalCard/VerticalCard';
import { useTheme } from '../../../theming/useTheme';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { CardComponentDataProps } from '../../partials/cards/dividedCard/DividedCard';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';

const componentConfigurationKey = 'scrollableArticleList';
const createStyles = (vrLineHeight: VrLineHeight) => StyleSheet.create({
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '1200px',
  },
  scrollContainer: {
    '-webkit-overflow-scrolling': 'touch',
    display: 'flex',
    justifyContent: 'space-between',
    overflowX: 'scroll',
    overflowY: 'visible',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
      overflow: 'visible',
      justifyContent: 'center',
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
    },
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  scrollItem: {
    flex: '0 0 auto',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 4}px`,
      flexBasis: `${300 - vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] / 4}px`,
      flexBasis: `${300 - vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 4}px`,
      flexBasis: `${300 - vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] / 2}px`,
    },
    position: 'relative',
    boxSizing: 'border-box',
    '@media (max-width: 330px)': {
      flexBasis: '85%',
    },
    ':first-child': {
      marginLeft: 0,
    },
    ':last-child': {
      marginRight: 0,
    },
  },
});

interface ScrollableArticleListComponentDataProps {
  cardsProps: CardComponentDataProps[]
}

interface ScrollableArticleListComponentThemeProps {
  vrLineHeight: VrLineHeight
}

type ScrollableArticleListComponentProps = ScrollableArticleListComponentDataProps & ScrollableArticleListComponentThemeProps;


export const ScrollableArticleListComponent: React.FunctionComponent<ScrollableArticleListComponentProps> = ({ cardsProps, vrLineHeight }) => {
  const styles = createStyles(vrLineHeight);
  const createCard = (props: CardComponentDataProps) => {
    const { id } = props;
    return (
      <div className={css(styles.scrollItem)} key={id}>
        <VerticalCard {...props} />
      </div>
    );
  };

  return (
    <div className={css(styles.listWrapper)}>
      <div className={css(styles.scrollContainer)}>
        {cardsProps.map(createCard)}
      </div>
    </div>
  );
};

ScrollableArticleListComponent.defaultProps = {
  cardsProps: [],
};

const mapThemeProps = (theme: ScrollableArticleListComponentThemeProps) => ({
  vrLineHeight: theme.vrLineHeight,
});

export const ScrollableArticleList = (props: ScrollableArticleListComponentDataProps) => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeProps);
  return <ErrorBoundary><ScrollableArticleListComponent {...themeContext} {...props} /></ErrorBoundary>;
};
